/**
 * Aspect — AssetRegistry
 * --------------------------------------------------------------
 * Resolves an asset_id to a render-entity factory. It attempts to load the
 * catalog glTF (container asset) once and caches it; if the glb is missing
 * (common during early development) it transparently falls back to a procedural
 * box placeholder sized to the asset footprint.
 *
 * Memory discipline: container assets are loaded ONCE and instanced; placeholder
 * materials are shared per category (never one material per instance).
 */

import * as pc from "playcanvas";

const CATEGORY_COLORS = {
  bedroom: [0.55, 0.62, 0.78],
  living: [0.72, 0.58, 0.5],
  dining: [0.6, 0.7, 0.55],
  office: [0.65, 0.65, 0.7],
  storage: [0.7, 0.66, 0.55],
  decor: [0.75, 0.55, 0.6],
  default: [0.7, 0.7, 0.72],
};

const DEFAULT_FOOTPRINT = { w: 1, h: 0.8, d: 1 };
function instOpts() {
  return { castShadows: true, receiveShadows: true };
}

export class AssetRegistry {
  constructor(app) {
    this.app = app;
    /** @type {Map<string, Promise<object>>} asset_id -> descriptor promise */
    this.cache = new Map();
    /** @type {Map<string, pc.StandardMaterial>} category -> shared material */
    this.placeholderMaterials = new Map();
    /** @type {Map<pc.Material, number>} emissive-mapped mats -> authored intensity
     *  (lamp shades, clock faces) — dimmed at daylight by LightSystem. */
    this.emissiveLampMats = new Map();
    // Streaming progress for the loading chip ("assets:progress" UI event).
    this._loadTotal = 0;
    this._loadDone = 0;
  }

  _progress(delta) {
    if (delta === "start") this._loadTotal += 1;
    else this._loadDone += 1;
    this.app.fire("assets:progress", { done: this._loadDone, total: this._loadTotal });
  }

  _placeholderMaterial(category) {
    const key = CATEGORY_COLORS[category] ? category : "default";
    if (this.placeholderMaterials.has(key)) return this.placeholderMaterials.get(key);
    const [r, g, b] = CATEGORY_COLORS[key];
    const mat = new pc.StandardMaterial();
    mat.diffuse = new pc.Color(r, g, b);
    mat.gloss = 0.3;
    mat.useMetalness = true;
    mat.metalness = 0.0;
    mat.update();
    this.placeholderMaterials.set(key, mat);
    return mat;
  }

  _normalizeFootprint(footprint) {
    const fp = footprint ?? DEFAULT_FOOTPRINT;
    return {
      w: fp.w > 0 ? fp.w : DEFAULT_FOOTPRINT.w,
      h: fp.h > 0 ? fp.h : DEFAULT_FOOTPRINT.h,
      d: fp.d > 0 ? fp.d : DEFAULT_FOOTPRINT.d,
    };
  }

  /** Ensure an asset is resolved (loaded or placeholder). Cached per asset_id + URL. */
  ensure(asset_id, glbUrl, footprint, category = "default") {
    const cacheKey = glbUrl ? `${asset_id}::${glbUrl}` : asset_id;
    if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);

    const fp = this._normalizeFootprint(footprint);

    const promise = new Promise((resolve) => {
      const fallback = () => resolve({ kind: "placeholder", footprint: fp, material: this._placeholderMaterial(category) });

      if (!glbUrl) return fallback();

      // Unique PlayCanvas asset name so a cache-busted URL actually reloads.
      const asset = new pc.Asset(cacheKey, "container", { url: glbUrl });
      this._progress("start");
      asset.once("load", () => {
        this._progress("done");
        if (asset.resource) {
          resolve({ kind: "glb", container: asset, footprint: fp });
          return;
        }
        this.app.assets.remove(asset);
        this.cache.delete(cacheKey);
        console.warn(`[assets] ${asset_id}: container loaded without resource (${glbUrl})`);
        fallback();
      });
      asset.once("error", () => {
        this._progress("done");
        this.app.assets.remove(asset);
        this.cache.delete(cacheKey);
        fallback();
      });
      this.app.assets.add(asset);
      this.app.assets.load(asset);
    });

    this.cache.set(cacheKey, promise);
    return promise;
  }

  /** True when the hierarchy has at least one visible mesh instance.
   *  Walks disabled nodes too — glTF roots often instantiate disabled until prepared. */
  _entityHasMesh(entity) {
    if (!entity) return false;
    let ok = false;
    const visit = (node) => {
      if (!node) return;
      for (const r of node.findComponents?.("render") ?? []) {
        if (r.enabled === false) continue;
        if (r.type === "box" && r.material) {
          ok = true;
          return;
        }
        for (const mi of r.meshInstances ?? []) {
          if (mi?.mesh) {
            ok = true;
            return;
          }
        }
      }
      for (const m of node.findComponents?.("model") ?? []) {
        if (m.enabled !== false && m.meshInstances?.length) {
          ok = true;
          return;
        }
      }
      for (const child of node.children ?? []) {
        if (ok) return;
        visit(child);
      }
    };
    visit(entity);
    return ok;
  }

  /** Make PBR materials visible under the scene's HDR environment. */
  _prepareMaterial(mat) {
    if (!mat) return;
    if (mat.opacity != null && mat.opacity < 0.05) mat.opacity = 1;
    // Unity→Blender GLBs often ship MASK + alpha 0 (fully discarded).
    if (mat.alphaTest > 0 && mat.opacity < 0.5) {
      mat.opacity = 1;
      mat.alphaTest = 0;
    }
    if ("useLighting" in mat) mat.useLighting = true;
    // Transparent glass: keep blend + skip skybox (env reflections read as pink mirrors).
    const isGlass =
      String(mat.name || "").toLowerCase().includes("glass") ||
      (mat.blendType === pc.BLEND_NORMAL && (mat.opacity ?? 1) < 0.95);
    if ("useSkybox" in mat) mat.useSkybox = !isGlass;
    const d = mat.diffuse;
    const lum = d ? d.r + d.g + d.b : 0;
    if (lum < 0.04 && !mat.diffuseMap && !mat.emissiveMap) {
      mat.diffuse = new pc.Color(0.72, 0.72, 0.76);
    }
    if (d && "a" in d && d.a < 0.05) {
      mat.diffuse = new pc.Color(d.r || 0.72, d.g || 0.72, d.b || 0.76);
      mat.opacity = 1;
    }
    if (!isGlass && mat.blendType === pc.BLEND_NORMAL && mat.opacity >= 0.99) {
      mat.blendType = pc.BLEND_NONE;
      mat.depthWrite = true;
    }
    // Register lamp-style emissive materials (glowing shades, clock faces) so
    // LightSystem can dim them in daylight and bring them up after dark.
    if (!isGlass && mat.emissiveMap && !this.emissiveLampMats.has(mat)) {
      const base = Number.isFinite(mat.emissiveIntensity) ? mat.emissiveIntensity : 1;
      if (base > 0.05) this.emissiveLampMats.set(mat, base);
    }
    mat.update();
  }

  /** Transparent glass must not block the realtime sun coming through windows. */
  _isGlassMaterial(mat) {
    if (!mat) return false;
    return (
      String(mat.name || "").toLowerCase().includes("glass") ||
      (mat.blendType === pc.BLEND_NORMAL && (mat.opacity ?? 1) < 0.95)
    );
  }

  /** Enable drawables and fix render-component state so meshes actually show. */
  _prepareDrawable(entity, resource = null) {
    if (!entity) return;
    entity.enabled = true;

    if (resource?.getMaterialVariants && resource?.applyMaterialVariant) {
      const variants = resource.getMaterialVariants();
      if (variants?.length) {
        try {
          resource.applyMaterialVariant(entity, variants[0]);
        } catch {
          /* best-effort */
        }
      }
    }

    const visit = (node) => {
      if (node._aspectContactShadow || String(node.name ?? "").startsWith("contact-shadow")) return;
      node.enabled = true;
      for (const r of node.findComponents?.("render") ?? []) {
        r.enabled = true;
        r.castShadows = true;
        r.receiveShadows = true;
        // Primitive types (box/sphere/…) override meshInstances. Only clear those —
        // never delete type "asset" (container/GLB meshes), which drops the mesh.
        const primTypes = new Set(["box", "sphere", "cylinder", "cone", "capsule", "plane"]);
        if (r.meshInstances?.length && primTypes.has(r.type)) {
          delete r.type;
        }
        if (r.material) this._prepareMaterial(r.material);
        for (const mi of r.meshInstances ?? []) {
          mi.visible = true;
          mi.receiveShadow = true;
          this._prepareMaterial(mi.material);
          mi.castShadow = !this._isGlassMaterial(mi.material);
        }
      }
      for (const m of node.findComponents?.("model") ?? []) {
        m.enabled = true;
        m.castShadows = true;
        for (const mi of m.meshInstances ?? []) {
          mi.visible = true;
          mi.receiveShadow = true;
          this._prepareMaterial(mi.material);
          mi.castShadow = !this._isGlassMaterial(mi.material);
        }
      }
      for (const child of node.children ?? []) visit(child);
    };
    visit(entity);
  }

  /** Re-enable drawables on a pooled instance (materials can stale after disable). */
  prepareDrawable(entity, resource = null) {
    this._prepareDrawable(entity, resource);
  }

  _makePlaceholder(asset_id, category, footprint = DEFAULT_FOOTPRINT) {
    const entity = new pc.Entity(`ph-${asset_id}`);
    entity.addComponent("render", {
      type: "box",
      material: this._placeholderMaterial(category),
      castShadows: true,
      receiveShadows: true,
    });
    entity._aspectKind = "placeholder";
    entity._aspectFootprint = this._normalizeFootprint(footprint);
    return entity;
  }

  _instantiateGlb(resource) {
    if (!resource) return null;
    if (typeof resource.instantiateRenderEntity === "function") {
      try {
        const entity = resource.instantiateRenderEntity(instOpts());
        if (entity) {
          this._prepareDrawable(entity, resource);
          return entity;
        }
      } catch (err) {
        console.warn("[assets] instantiateRenderEntity failed", err);
      }
    }
    if (typeof resource.instantiateModelEntity === "function") {
      try {
        const entity = resource.instantiateModelEntity(instOpts());
        if (entity) {
          this._prepareDrawable(entity, resource);
          return entity;
        }
      } catch (err) {
        console.warn("[assets] instantiateModelEntity failed", err);
      }
    }
    return null;
  }

  /**
   * Create a fresh render entity for an asset. For glb assets this instances the
   * shared container (geometry/material reused). For placeholders it builds a
   * unit box (scaled later by the transform layer) with a shared material.
   */
  async create(asset_id, glbUrl, footprint, category = "default") {
    const fp = this._normalizeFootprint(footprint);
    const cacheKey = glbUrl ? `${asset_id}::${glbUrl}` : asset_id;
    const desc = await this.ensure(asset_id, glbUrl, fp, category);

    if (desc.kind === "glb") {
      const resource = desc.container?.resource;
      if (!resource) {
        this.cache.delete(cacheKey);
        console.warn(`[assets] ${asset_id}: missing container resource — using placeholder (${glbUrl})`);
        return this._makePlaceholder(asset_id, category, fp);
      }

      const entity = this._instantiateGlb(resource);
      if (entity && this._entityHasMesh(entity)) {
        entity._aspectKind = "glb";
        entity._aspectContainerResource = resource;
        return entity;
      }

      entity?.destroy?.();
      this.cache.delete(cacheKey);
      console.warn(`[assets] ${asset_id}: empty glb — using placeholder (${glbUrl})`);
    }

    return this._makePlaceholder(asset_id, category, desc.footprint ?? fp);
  }

  /** Drop a cached GLB so Safari can reclaim GPU textures. */
  unloadAsset(asset_id) {
    if (!asset_id) return;
    for (const key of [...this.cache.keys()]) {
      if (key !== asset_id && !key.startsWith(`${asset_id}::`)) continue;
      const pending = this.cache.get(key);
      this.cache.delete(key);
      void Promise.resolve(pending).then((desc) => {
        const asset = desc?.container;
        if (!asset) return;
        try {
          this.app.assets.remove(asset);
          asset.unload?.();
        } catch {
          /* ignore */
        }
      });
    }
  }

  destroy() {
    for (const mat of this.placeholderMaterials.values()) mat.destroy();
    this.placeholderMaterials.clear();
    this.cache.clear();
  }
}

export default AssetRegistry;
